import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Star, Clock, Phone } from 'lucide-react';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom red pin marker icon (DOCKHTA brand)
const createCustomIcon = (isSelected = false) => {
  const iconHtml = `
    <div style="
      position: relative;
      width: 36px;
      height: 48px;
    ">
      <svg viewBox="0 0 36 48" width="36" height="48" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 0C8.059 0 0 8.059 0 18c0 13.5 18 30 18 30s18-16.5 18-30c0-9.941-8.059-18-18-18z" 
              fill="${isSelected ? '#1A237E' : '#E53935'}" />
        <circle cx="18" cy="16" r="8" fill="white" />
        <path d="M15 16h6M18 13v6" stroke="${isSelected ? '#1A237E' : '#E53935'}" stroke-width="2" stroke-linecap="round" />
      </svg>
      ${isSelected ? '<div style="position: absolute; top: -8px; left: -8px; width: 52px; height: 52px; border-radius: 50%; background: rgba(26, 35, 126, 0.2); animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>' : ''}
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-marker',
    iconSize: [36, 48],
    iconAnchor: [18, 48],
    popupAnchor: [0, -48],
  });
};

// Map center change handler
const MapCenterHandler = ({ center }) => {
  const map = useMap();
  React.useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Professional Popup Component
const ProfessionalPopup = ({ professional, onSelect }) => {
  const user = professional.user || {};
  
  return (
    <div className="min-w-[250px] p-1">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E53935] to-[#D32F2F] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
          {user.full_name?.charAt(0) || 'D'}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#1A237E] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dr. {user.full_name || 'Professionnel'}
          </h3>
          <p className="text-sm text-[#2196F3] font-medium">
            {professional.specialty || 'Medecine Generale'}
          </p>
          {professional.rating > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <Star className="w-4 h-4 text-amber-400 fill-current" />
              <span className="text-sm font-medium text-slate-700">
                {professional.rating.toFixed(1)}
              </span>
              <span className="text-xs text-slate-500">
                ({professional.total_reviews} avis)
              </span>
            </div>
          )}
        </div>
      </div>
      
      {professional.address && (
        <div className="flex items-start gap-2 mt-3 text-sm text-slate-600">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#E53935]" />
          <span className="line-clamp-2">{professional.address}</span>
        </div>
      )}
      
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-1 text-sm">
          <span className="font-semibold text-[#1A237E]">
            {professional.consultation_fee?.toLocaleString() || '0'} FCFA
          </span>
          <span className="text-slate-500">/ consultation</span>
        </div>
      </div>
      
      {onSelect && (
        <button
          onClick={() => onSelect(professional)}
          className="w-full mt-3 py-2 px-4 bg-[#E53935] hover:bg-[#D32F2F] text-white text-sm font-semibold rounded-xl transition-all"
          data-testid={`map-select-${professional.id}`}
        >
          Voir le profil
        </button>
      )}
    </div>
  );
};

// Main Map Component
const ProfessionalsMap = ({
  professionals = [],
  center = [3.8480, 11.5021], // Yaoundé, Cameroon by default
  zoom = 12,
  selectedId = null,
  onProfessionalSelect,
  className = '',
  height = '400px',
}) => {
  return (
    <div 
      className={`map-container overflow-hidden ${className}`} 
      style={{ height }}
      data-testid="professionals-map"
    >
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%', borderRadius: '1rem' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapCenterHandler center={center} />
        
        {professionals.map((professional) => {
          if (!professional.latitude || !professional.longitude) return null;
          
          const isSelected = selectedId === professional.id;
          
          return (
            <Marker
              key={professional.id}
              position={[professional.latitude, professional.longitude]}
              icon={createCustomIcon(isSelected)}
            >
              <Popup className="custom-popup">
                <ProfessionalPopup 
                  professional={professional} 
                  onSelect={onProfessionalSelect}
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

// Mini Map for Professional Detail
export const ProfessionalMiniMap = ({ 
  latitude, 
  longitude, 
  address,
  className = '',
  height = '200px' 
}) => {
  if (!latitude || !longitude) {
    return (
      <div 
        className={`bg-slate-100 rounded-2xl flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-slate-500">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Localisation non disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`map-container overflow-hidden ${className}`} 
      style={{ height }}
      data-testid="mini-map"
    >
      <MapContainer
        center={[latitude, longitude]}
        zoom={15}
        scrollWheelZoom={false}
        dragging={false}
        style={{ height: '100%', width: '100%', borderRadius: '1rem' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker 
          position={[latitude, longitude]} 
          icon={createCustomIcon(true)}
        >
          {address && (
            <Popup>
              <div className="text-sm">
                <MapPin className="w-4 h-4 inline mr-1 text-[#E53935]" />
                {address}
              </div>
            </Popup>
          )}
        </Marker>
      </MapContainer>
    </div>
  );
};

export default ProfessionalsMap;
